import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Payment } from '@//app/api/commerce/payments/[id]/list';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { CreditWarningSweden } from '@//components/CreditWarningSweden';
import { SegmentedRadioGroup, SegmentedRadioGroupItem } from '@//components/SegmentedRadioGroup';
import { SummaryBox } from '@//components/SummaryBox';
import { TextContentHtml } from '@//components/TextContent';
import { VStack } from '@//components/VStack';
import { RangeSliderTextfield } from '@//containers/RangeSliderTextfield';
import useModifyPayment from '@//data/ecom/useModifyPayment';
import { useLegacyTrackFinanceInterested } from '@//data/tracking/useLegacyTrackFinanceInterested';
import useDebouncedValue from '@//hooks/useDebouncedValue';
import { useTriggerOnce } from '@//hooks/useTriggerOnce';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency, getCurrencySuffix } from '@//utils/currency';
import { markdownToHtml } from '@//utils/text';
import { WidgetHeading } from '../../../WidgetHeading';
import styles from './Loan.module.scss';
import { LoanDetails } from './LoanDetails';
import {
  createDefaultValues,
  createDurationSteps,
  createIndexedPayments,
  createModifiedPayload,
  type PaymentForm,
} from './utils';

interface LoanProps {
  payments: Payment[];
  id: string;
  branchId: string;
  vehicle: ElasticVehicle;
}

export const Loan = ({ payments, branchId, id, vehicle }: LoanProps) => {
  const t = useTranslations('pages.item.financeSection.loan');
  const locale = useLocale();
  const marketMetadata = useMarketMetadata();
  const { trackLegacyFinanceInterested } = useLegacyTrackFinanceInterested();

  const paymentList = useMemo(() => createIndexedPayments(payments), [payments]);

  const imageUrl = useMemo(
    () => (paymentList.length > 0 ? paymentList[0].imageUrl : undefined),
    [paymentList],
  );

  const defaultValues = useMemo(() => createDefaultValues(paymentList[0]), [paymentList]);
  const { control, register, reset, setValue, getValues } = useForm<PaymentForm>({ defaultValues });

  const [activeField, setActiveField] = useState<string | null>(null);

  useEffect(() => {
    reset(createDefaultValues(paymentList[0]));
  }, [paymentList]);

  const generatedId = useWatch({
    control,
    name: 'payment.generatedId',
  });
  const selectedPaymentTemplate = useMemo(
    () => paymentList.find((p) => p.generatedId === generatedId),
    [generatedId, paymentList],
  );

  const payment = useWatch({
    control,
    name: 'payment',
  });

  const trackFinanceInteraction = useTriggerOnce(() => {
    trackLegacyFinanceInterested({
      vehicle,
      information: {
        financialInstitutions: payment?.financialInstitution,
      },
    });
  });

  const {
    monthlyCost,
    duration: { current: currentDuration },
    downPayment: {
      current: paymentCurrent,
      min: downPaymentMin,
      max: downPaymentMax,
      step: downPaymentStep,
    },
  } = payment;

  const downPaymentMinPercentage = Math.round((downPaymentMin / payment.vehiclePrice) * 100);
  const downPaymentMaxPercentage = Math.round((downPaymentMax / payment.vehiclePrice) * 100);

  const residualCurrent = payment.residual?.current ?? 0;
  const residualMin = payment.residual?.min ?? 0;
  const residualMax = payment.residual?.max ?? 0;
  const residualStep = payment.residual?.step ?? 0;
  const residualDefault = payment.residual?.default ?? 0;

  const onChangeDownPayment = useCallback(
    ([value]: number[]) => {
      trackFinanceInteraction();
      setValue('payment.downPayment.current', value);
    },
    [trackFinanceInteraction],
  );

  const onChangeResidual = useCallback(
    ([value]: number[]) => {
      trackFinanceInteraction();
      setValue('payment.residual.current', value);
    },
    [trackFinanceInteraction],
  );

  const modifiedPayload = useMemo(
    () => createModifiedPayload(id, branchId, payment),
    [payment, id, branchId],
  );

  const debouncedModifiedPayload = useDebouncedValue(modifiedPayload, 200);
  const modifyPaymentQuery = useModifyPayment(debouncedModifiedPayload);
  const updatedPayment = modifyPaymentQuery.data?.response;

  useEffect(() => {
    if (updatedPayment) {
      const currentValues = getValues();
      setValue('payment', {
        ...updatedPayment,
        // Preserve the field user is actively editing
        downPayment: {
          ...updatedPayment.downPayment,
          current:
            activeField === 'downPayment'
              ? currentValues.payment.downPayment.current
              : updatedPayment.downPayment.current,
        },
        residual: updatedPayment.residual
          ? {
              ...updatedPayment.residual,
              current:
                activeField === 'residual'
                  ? (currentValues.payment.residual?.current ?? updatedPayment.residual.current)
                  : updatedPayment.residual.current,
            }
          : undefined,
      });
    }
  }, [updatedPayment, activeField]);

  const durationSteps = useMemo(() => createDurationSteps(payment.duration), [payment.duration]);

  const onSelectPaymentPreset = useCallback(
    (paymentForm: PaymentForm) => {
      trackFinanceInteraction();
      reset(paymentForm);
    },
    [trackFinanceInteraction],
  );

  const onBlurDownPayment = useCallback(() => {
    setActiveField(null);
    const value = getValues('payment.downPayment.current');
    if (Number.isNaN(value)) setValue('payment.downPayment.current', downPaymentMin);
    else if (value > downPaymentMax) setValue('payment.downPayment.current', downPaymentMax);
  }, [downPaymentMin, downPaymentMax]);

  const onBlurResidual = useCallback(() => {
    setActiveField(null);
    const value = getValues('payment.residual.current');
    if (value === undefined || Number.isNaN(value))
      setValue('payment.residual.current', residualMin);
    else if (value > residualMax) setValue('payment.residual.current', residualMax);
  }, [residualMin, residualMax]);

  const onFocusDownPayment = useCallback(() => {
    trackFinanceInteraction();
    setActiveField('downPayment');
  }, [trackFinanceInteraction]);

  const onFocusResidual = useCallback(() => {
    trackFinanceInteraction();
    setActiveField('residual');
  }, [trackFinanceInteraction]);

  return (
    <VStack gap={32}>
      <WidgetHeading
        logotype={
          imageUrl
            ? {
                src: `${imageUrl}`,
                srcSet: `${imageUrl}`,
                alt: t('logoAlt'),
              }
            : undefined
        }
      >
        {t('heading')}
      </WidgetHeading>
      <VStack gap={24}>
        <form className={styles.form}>
          {paymentList && (
            <SegmentedRadioGroup id="finance-loan-preset" label={t('typeLabel')} layout="fullWidth">
              {paymentList.map((p, index) => (
                <SegmentedRadioGroupItem
                  key={p.generatedId}
                  name="finance-loan-preset"
                  id={`finance-loan-preset-${index}`}
                  value={p.name}
                  checked={payment.generatedId === p.generatedId}
                  onChange={() => onSelectPaymentPreset({ payment: p })}
                  meta={formatCurrency(p.monthlyCost, {
                    locale,
                    currency: marketMetadata.currency,
                    period: 'month',
                  })}
                >
                  {p.name}
                </SegmentedRadioGroupItem>
              ))}
            </SegmentedRadioGroup>
          )}
          {selectedPaymentTemplate?.description && (
            <TextContentHtml
              textSize="sm"
              className={styles.description}
              html={markdownToHtml(selectedPaymentTemplate.description)}
            />
          )}
          {!selectedPaymentTemplate?.duration.fixed && (
            <SegmentedRadioGroup id="finance-loan-installment-period" label={t('fixedTerm.label')}>
              {durationSteps.map((step) => (
                <SegmentedRadioGroupItem
                  key={step}
                  name="finance-loan-installment-period"
                  id={`finance-loan-installment-period-${step}`}
                  value={step.toString()}
                  checked={currentDuration === step}
                  onChange={(e) => {
                    trackFinanceInteraction();
                    setValue('payment.duration.current', parseInt(e.currentTarget.value, 10));
                  }}
                >
                  {step} {t('fixedTerm.unit')}
                </SegmentedRadioGroupItem>
              ))}
            </SegmentedRadioGroup>
          )}
          {!selectedPaymentTemplate?.downPayment.fixed && (
            <RangeSliderTextfield
              id="finance-loan-down-payment"
              label={t('downPaymentLabel')}
              rangeSlider={{
                id: 'finance-loan-down-payment-range-slider',
                label: t('downPaymentLabel'),
                minLabel: `${downPaymentMinPercentage}%`,
                maxLabel: `${downPaymentMaxPercentage}%`,
                min: downPaymentMin,
                max: downPaymentMax,
                step: downPaymentStep,
                defaultValue: Number.isNaN(paymentCurrent) ? [downPaymentMin] : [paymentCurrent],
                value: [Number.isNaN(paymentCurrent) ? downPaymentMin : paymentCurrent],
                onChange: onChangeDownPayment,
              }}
              textfield={{
                id: 'finance-loan-down-payment-textfield',
                type: 'number',
                label: t('downPaymentLabel'),
                unit: getCurrencySuffix(marketMetadata.currency, locale),
                onFocus: onFocusDownPayment,
                defaultValue: Number.isNaN(paymentCurrent) ? downPaymentMin : paymentCurrent,
                ...register('payment.downPayment.current', {
                  valueAsNumber: true,
                  onBlur: onBlurDownPayment,
                }),
              }}
            />
          )}
          {payment.residual && !selectedPaymentTemplate?.residual?.fixed && (
            <RangeSliderTextfield
              id="finance-loan-residual"
              label={t('residualLabel')}
              rangeSlider={{
                id: 'finance-loan-residual-range-slider',
                label: t('residualLabel'),
                minLabel: `${residualMin}%`,
                maxLabel: `${residualMax}%`,
                min: residualMin,
                max: residualMax,
                step: residualStep,
                defaultValue: Number.isNaN(residualDefault) ? [residualMin] : [residualDefault],
                value: [Number.isNaN(residualCurrent) ? residualMin : residualCurrent],
                onChange: onChangeResidual,
              }}
              textfield={{
                id: 'finance-loan-residual-textfield',
                type: 'number',
                label: t('residualLabel'),
                unit: '%',
                onFocus: onFocusResidual,
                defaultValue: Number.isNaN(residualCurrent) ? residualMin : residualCurrent,
                ...register('payment.residual.current', {
                  valueAsNumber: true,
                  onBlur: onBlurResidual,
                }),
              }}
            />
          )}
        </form>
        <SummaryBox
          label={t('monthlyCostLabel')}
          value={formatCurrency(monthlyCost, {
            locale,
            currency: marketMetadata.currency,
            period: 'month',
          })}
        />
        <LoanDetails payment={updatedPayment || payment} />
        <CreditWarningSweden headingLevel={3} boxed />
      </VStack>
    </VStack>
  );
};
